import { useCallback, useEffect, useState, useMemo } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  KeyRound,
  X,
  Copy,
  Shield,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Download,
  Mail,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { getApiErrorKey } from '../../utils/apiError'
import type { AdminUser, AdminCustomer, AdminERP, UserPermissions } from '../../types'
import { formatDate } from '../../utils/helpers'
import PasswordInput from '../../components/PasswordInput'
import PhoneInput from '../../components/PhoneInput'
import { exportToCSV } from '../../utils/csv'

interface UserFormData {
  email: string
  password: string
  firstname: string
  lastname: string
  phone: string
  customer_id: number | ''
  erp_id: number | '' // ERP managed by admin role users
  role: string
  is_active: boolean
}

const emptyForm: UserFormData = {
  email: '',
  password: '',
  firstname: '',
  lastname: '',
  phone: '',
  customer_id: '',
  erp_id: '',
  role: 'user',
  is_active: true,
}

type SortKey = 'email' | 'name' | 'customer_name' | 'role' | 'is_active' | 'last_login'
type SortDir = 'asc' | 'desc'

function SortIcon({
  col,
  sortBy,
  sortDir,
}: {
  col: SortKey
  sortBy: SortKey | null
  sortDir: SortDir
}) {
  if (sortBy !== col) return <ChevronsUpDown className="w-3 h-3 ml-1 inline opacity-40" />
  return sortDir === 'asc' ? (
    <ChevronUp className="w-3 h-3 ml-1 inline text-primary-600" />
  ) : (
    <ChevronDown className="w-3 h-3 ml-1 inline text-primary-600" />
  )
}

export default function AdminUsers() {
  const { t } = useTranslation()
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}')
    } catch {
      return {}
    }
  })()
  const isSuperAdmin = currentUser?.role === 'super_admin'
  const roleLabel = (role: string) =>
    ({
      user: t('admin.users.roleCustomer'),
      admin: t('admin.users.roleAdmin'),
      super_admin: t('admin.users.roleSuperAdmin'),
    })[role] ?? role

  const [users, setUsers] = useState<AdminUser[]>([])
  const [customers, setCustomers] = useState<AdminCustomer[]>([])
  const [erps, setErps] = useState<AdminERP[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<UserFormData>(emptyForm)
  const [originalRole, setOriginalRole] = useState<string>('')
  const [roleConfirm, setRoleConfirm] = useState<{
    oldRole: string
    newRole: string
    userLabel: string
  } | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [resetPasswordResult, setResetPasswordResult] = useState<{
    id: number
    password: string
  } | null>(null)
  const [resetEmailSentFor, setResetEmailSentFor] = useState<string | null>(null)

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Sorting
  const [sortBy, setSortBy] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  // Permissions panel
  const [permissionsUser, setPermissionsUser] = useState<AdminUser | null>(null)
  const [permissions, setPermissions] = useState<UserPermissions | null>(null)
  const [permissionsLoading, setPermissionsLoading] = useState(false)

  const loadData = useCallback(async () => {
    try {
      const [usersData, customersData, erpsData] = await Promise.all([
        api.getAdminUsers(),
        api.getAdminCustomers(),
        api.getAdminERPs(),
      ])
      setUsers(usersData)
      setCustomers(customersData)
      setErps(Array.isArray(erpsData) ? erpsData : [])
    } catch {
      setError(t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadData()
  }, [loadData])

  const loadUsers = async () => {
    const data = await api.getAdminUsers()
    setUsers(data)
    setSelectedIds(new Set())
  }

  // ---- Sorting ----
  const handleSort = (col: SortKey) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortDir('asc')
    }
  }

  const sortedUsers = useMemo(() => {
    if (!sortBy) return users
    return [...users].sort((a, b) => {
      let va: string | number = ''
      let vb: string | number = ''
      if (sortBy === 'email') {
        va = a.email ?? ''
        vb = b.email ?? ''
      } else if (sortBy === 'name') {
        va = [a.firstname, a.lastname].filter(Boolean).join(' ')
        vb = [b.firstname, b.lastname].filter(Boolean).join(' ')
      } else if (sortBy === 'customer_name') {
        // Show ERP name for admin users
        va = (a.role === 'admin' ? a.erp_name : a.customer_name) ?? ''
        vb = (b.role === 'admin' ? b.erp_name : b.customer_name) ?? ''
      } else if (sortBy === 'role') {
        va = a.role ?? ''
        vb = b.role ?? ''
      } else if (sortBy === 'is_active') {
        va = a.is_active ? 1 : 0
        vb = b.is_active ? 1 : 0
      } else if (sortBy === 'last_login') {
        va = a.last_login ?? ''
        vb = b.last_login ?? ''
      }
      if (typeof va === 'number')
        return sortDir === 'asc' ? va - (vb as number) : (vb as number) - va
      return sortDir === 'asc' ? va.localeCompare(vb as string) : (vb as string).localeCompare(va)
    })
  }, [users, sortBy, sortDir])

  // ---- Selection ----
  const allSelected = sortedUsers.length > 0 && selectedIds.size === sortedUsers.length
  const someSelected = selectedIds.size > 0 && !allSelected

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(sortedUsers.map((u) => u.id)))
    }
  }

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ---- CRUD ----
  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setShowModal(true)
    setError('')
  }

  const openEdit = (user: AdminUser) => {
    setEditingId(user.id)
    setOriginalRole(user.role)
    setForm({
      email: user.email,
      password: '',
      firstname: user.firstname ?? '',
      lastname: user.lastname ?? '',
      phone: user.phone ?? '',
      customer_id: user.customer_id ?? '',
      erp_id: user.erp_id ?? '',
      role: user.role,
      is_active: user.is_active,
    })
    setShowModal(true)
    setError('')
  }

  const doSave = async () => {
    setSaving(true)
    setError('')
    try {
      const payload: Record<string, unknown> = { ...form }
      if (!payload.password) delete payload.password
      // erp_id only relevant when role = 'admin'; clear for other roles
      if (payload.role !== 'admin') delete payload.erp_id
      if (editingId) {
        await api.updateAdminUser(editingId, payload)
      } else {
        await api.createAdminUser(payload)
      }
      setShowModal(false)
      await loadUsers()
    } catch (err: unknown) {
      setError(getApiErrorKey(err, 'common.error'))
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    // If editing and role changed, ask for confirmation first
    if (editingId && form.role !== originalRole) {
      const userLabel = [form.firstname, form.lastname].filter(Boolean).join(' ') || form.email
      setRoleConfirm({ oldRole: originalRole, newRole: form.role, userLabel })
      return
    }
    await doSave()
  }

  const handleDelete = async (user: AdminUser) => {
    if (!confirm(t('admin.users.deleteConfirm', { name: user.email }))) return
    try {
      await api.deleteAdminUser(user.id)
      await loadUsers()
    } catch (err: unknown) {
      alert(t(getApiErrorKey(err, 'common.error')))
    }
  }

  const handleDeleteSelected = async () => {
    const count = selectedIds.size
    if (!confirm(t('admin.users.deleteSelectedConfirm', { count }))) return
    try {
      await api.deleteAdminUsers(Array.from(selectedIds))
      await loadUsers()
    } catch (err: unknown) {
      alert(t(getApiErrorKey(err, 'common.error')))
    }
  }

  const handleResetPassword = async (user: AdminUser) => {
    if (!confirm(t('admin.users.resetPassword') + ' "' + user.email + '" ?')) return
    try {
      const result = await api.resetAdminUserPassword(user.id)
      setResetPasswordResult({ id: user.id, password: result.password })
    } catch (err: unknown) {
      alert(t(getApiErrorKey(err, 'common.error')))
    }
  }

  const copyPassword = (password: string) => {
    navigator.clipboard.writeText(password)
  }

  const handleSendResetEmail = async (user: AdminUser) => {
    try {
      await api.sendAdminUserResetEmail(user.id)
      setResetEmailSentFor(user.email)
      setTimeout(() => setResetEmailSentFor(null), 5000)
    } catch (err: unknown) {
      alert(t(getApiErrorKey(err, 'common.error')))
    }
  }

  // ---- Permissions panel ----
  const openPermissions = async (user: AdminUser) => {
    setPermissionsUser(user)
    setPermissions(null)
    setPermissionsLoading(true)
    try {
      const data = await api.getUserPermissions(user.id)
      setPermissions(data)
    } catch {
      setPermissions(null)
    } finally {
      setPermissionsLoading(false)
    }
  }

  const closePermissions = () => {
    setPermissionsUser(null)
    setPermissions(null)
  }

  const handleExportUsersCSV = () => {
    const rows = sortedUsers.map((u: AdminUser) => ({
      id: u.id,
      email: u.email,
      firstname: u.firstname ?? '',
      lastname: u.lastname ?? '',
      phone: u.phone ?? '',
      role: u.role,
      is_active: u.is_active ? '1' : '0',
      customer_name: u.customer_name ?? '',
      erp_name: u.erp_name ?? '',
      last_login: u.last_login ?? '',
    }))
    exportToCSV(rows, `utilisateurs_${new Date().toISOString().slice(0, 10)}.csv`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const thClass = (_col: SortKey) =>
    `text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 whitespace-nowrap`

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.users.title')}</h1>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              <Trash2 className="w-4 h-4" />
              <span className="sm:hidden">{selectedIds.size}</span>
              <span className="hidden sm:inline">
                {t('admin.users.deleteSelected', { count: selectedIds.size })}
              </span>
            </button>
          )}
          <button
            onClick={handleExportUsersCSV}
            disabled={!sortedUsers.length}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />{' '}
            <span className="hidden sm:inline">{t('common.exportCsv')}</span>
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />{' '}
            <span className="hidden sm:inline">{t('admin.users.addUser')}</span>
          </button>
        </div>
      </div>

      {/* Reset email sent confirmation */}
      {resetEmailSentFor && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg flex items-center justify-between">
          <p className="text-sm font-medium text-green-800">
            {t('admin.users.sendResetEmailSuccess', { email: resetEmailSentFor })}
          </p>
          <button
            onClick={() => setResetEmailSentFor(null)}
            className="p-1 text-green-600 hover:bg-green-100 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Reset password result */}
      {resetPasswordResult && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800">
                {t('admin.users.resetPassword')} :
              </p>
              <p className="text-lg font-mono font-bold text-blue-900 mt-1">
                {resetPasswordResult.password}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => copyPassword(resetPasswordResult.password)}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                title={t('common.save')}
              >
                <Copy className="w-5 h-5" />
              </button>
              <button
                onClick={() => setResetPasswordResult(null)}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                title={t('common.close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Mobile card layout */}
        <ul className="divide-y divide-gray-100 sm:hidden">
          {sortedUsers.length === 0 ? (
            <li className="p-6 text-center text-gray-500">{t('admin.users.none')}</li>
          ) : (
            sortedUsers.map((user) => (
              <li key={user.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate text-sm">{user.email}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {[user.firstname, user.lastname].filter(Boolean).join(' ') ||
                        t('common.none')}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        user.role === 'super_admin'
                          ? 'bg-red-100 text-red-800'
                          : user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {user.role === 'super_admin'
                        ? t('admin.users.roleSuperAdmin')
                        : user.role === 'admin'
                          ? t('admin.users.roleAdmin')
                          : t('admin.users.roleCustomer')}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-600'}`}
                    >
                      {user.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-500 truncate min-w-0">
                    {user.role === 'admin' && user.erp_name ? (
                      <span className="text-purple-600 font-medium">{user.erp_name}</span>
                    ) : (
                      (user.customer_name ?? '')
                    )}
                  </p>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openPermissions(user)}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"
                      title={t('admin.users.viewPermissions')}
                    >
                      <Shield className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleSendResetEmail(user)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                      title={t('admin.users.sendResetEmail')}
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetPassword(user)}
                      className="p-1.5 text-gray-400 hover:text-orange-600 rounded"
                      title={t('admin.users.resetPassword')}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEdit(user)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                      title={t('common.edit')}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>

        {/* Desktop table layout */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {/* Checkbox select-all */}
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected
                    }}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className={thClass('email')} onClick={() => handleSort('email')}>
                  {t('admin.users.colEmail')}{' '}
                  <SortIcon col="email" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className={thClass('name')} onClick={() => handleSort('name')}>
                  {t('admin.users.colName')}{' '}
                  <SortIcon col="name" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th
                  className={thClass('customer_name')}
                  onClick={() => handleSort('customer_name')}
                >
                  {t('admin.users.colSupplier')} / {t('admin.users.colErp')}{' '}
                  <SortIcon col="customer_name" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className={thClass('role')} onClick={() => handleSort('role')}>
                  {t('admin.users.colRole')}{' '}
                  <SortIcon col="role" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className={thClass('is_active')} onClick={() => handleSort('is_active')}>
                  {t('admin.users.colStatus')}{' '}
                  <SortIcon col="is_active" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className={thClass('last_login')} onClick={() => handleSort('last_login')}>
                  {t('admin.users.colLastLogin')}{' '}
                  <SortIcon col="last_login" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.users.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    {t('admin.users.none')}
                  </td>
                </tr>
              ) : (
                sortedUsers.map((user) => (
                  <tr
                    key={user.id}
                    className={`hover:bg-gray-50 ${selectedIds.has(user.id) ? 'bg-primary-50' : ''}`}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(user.id)}
                        onChange={() => toggleSelect(user.id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-4 font-medium text-gray-900 text-sm">{user.email}</td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {[user.firstname, user.lastname].filter(Boolean).join(' ') ||
                        t('common.none')}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {user.role === 'admin' && user.erp_name ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-purple-600 font-medium">{user.erp_name}</span>
                        </span>
                      ) : (
                        (user.customer_name ?? t('common.none'))
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                          user.role === 'super_admin'
                            ? 'bg-red-100 text-red-800'
                            : user.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {user.role === 'super_admin'
                          ? t('admin.users.roleSuperAdmin')
                          : user.role === 'admin'
                            ? t('admin.users.roleAdmin')
                            : t('admin.users.roleCustomer')}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-600'}`}
                      >
                        {user.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {user.last_login ? formatDate(user.last_login) : t('admin.users.never')}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openPermissions(user)}
                          className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                          title={t('admin.users.viewPermissions')}
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSendResetEmail(user)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title={t('admin.users.sendResetEmail')}
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="p-2 text-gray-400 hover:text-orange-600 transition-colors"
                          title={t('admin.users.resetPassword')}
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEdit(user)}
                          className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                          title={t('common.edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title={t('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? t('admin.users.modalEdit') : t('admin.users.modalCreate')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{t(error)}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.users.fieldEmail')}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.users.fieldPassword')}{' '}
                  {editingId && `(${t('admin.users.fieldPasswordHint')})`}
                </label>
                <PasswordInput
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input
                    type="text"
                    value={form.firstname}
                    onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.users.fieldName')}
                  </label>
                  <input
                    type="text"
                    value={form.lastname}
                    onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <PhoneInput
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                label={t('common.phone')}
                labelCls="block text-sm font-medium text-gray-700 mb-1"
                fieldCls="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.users.fieldSupplier')}
                </label>
                <select
                  value={form.customer_id}
                  onChange={(e) =>
                    setForm({ ...form, customer_id: e.target.value ? Number(e.target.value) : '' })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">-- Aucun (admin sans client) --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.customer_name} ({c.erp_name})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.users.fieldRole')}
                </label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value, erp_id: '' })}
                  disabled={isSuperAdmin && editingId === currentUser?.id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <option value="user">{t('admin.users.roleCustomer')}</option>
                  {isSuperAdmin && <option value="admin">{t('admin.users.roleAdmin')}</option>}
                  {isSuperAdmin && (
                    <option value="super_admin">{t('admin.users.roleSuperAdmin')}</option>
                  )}
                </select>
                {isSuperAdmin && editingId === currentUser?.id && (
                  <p className="mt-1 text-xs text-gray-700">{t('admin.users.cannotEditOwnRole')}</p>
                )}
              </div>
              {/* ERP selector: shown when role = 'admin' (ERP admin), super_admin only */}
              {isSuperAdmin && form.role === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.users.fieldErp')}
                  </label>
                  <select
                    value={form.erp_id}
                    onChange={(e) =>
                      setForm({ ...form, erp_id: e.target.value ? Number(e.target.value) : '' })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t('admin.users.fieldErpPlaceholder')}</option>
                    {erps.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.erp_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  {t('admin.users.fieldActive')}
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.email || (!editingId && !form.password)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions panel */}
      {permissionsUser && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.users.permissionsTitle', {
                  name:
                    [permissionsUser.firstname, permissionsUser.lastname]
                      .filter(Boolean)
                      .join(' ') || permissionsUser.email,
                })}
              </h2>
              <button onClick={closePermissions} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {permissionsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : permissions ? (
                <>
                  {/* ERP access */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                      {t('admin.users.permissionsErpAccess')}
                    </h3>
                    {permissions.erp ? (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                        <p className="font-medium text-gray-900">{permissions.erp.erp_name}</p>
                        <p className="text-sm text-gray-500">
                          {t('admin.users.permissionsErpType', { type: permissions.erp.erp_type })}
                        </p>
                        {permissions.erp.customer_name && (
                          <p className="text-sm text-gray-500">{permissions.erp.customer_name}</p>
                        )}
                        {permissions.erp.erp_customer_id && (
                          <p className="text-sm text-gray-400">
                            {t('admin.users.permissionsCustomer', {
                              id: permissions.erp.erp_customer_id,
                            })}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        {t('admin.users.permissionsNoErp')}
                      </p>
                    )}
                  </div>

                  {/* Groups & rights */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                      {t('admin.users.permissionsGroups')}
                    </h3>
                    {permissions.groups.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">
                        {t('admin.users.permissionsNoGroups')}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {permissions.groups.map((group) => (
                          <div
                            key={group.id}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                          >
                            <div
                              className={`px-4 py-2 flex items-center justify-between ${group.is_active ? 'bg-indigo-50' : 'bg-gray-50'}`}
                            >
                              <span className="font-medium text-sm text-gray-800">
                                {group.group_name}
                              </span>
                              {!group.is_active && (
                                <span className="text-xs text-gray-400 italic">
                                  {t('common.inactive')}
                                </span>
                              )}
                            </div>
                            {group.rights.length > 0 ? (
                              <ul className="divide-y divide-gray-100">
                                {group.rights.map((right) => (
                                  <li
                                    key={right.module_name}
                                    className="px-4 py-2 flex items-center justify-between gap-4"
                                  >
                                    <span className="text-sm text-gray-700">{right.label}</span>
                                    <span className="text-xs text-gray-400 shrink-0">
                                      {right.allowed_statuses === null
                                        ? t('admin.users.permissionsAllStatuses')
                                        : t('admin.users.permissionsCustomStatuses', {
                                            list: right.allowed_statuses.join(', '),
                                          })}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="px-4 py-2 text-xs text-gray-400 italic">
                                {t('common.none')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-red-500">{t('common.error')}</p>
              )}
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={closePermissions}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role change confirmation modal */}
      {roleConfirm !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="h-1.5 w-full bg-gradient-to-r from-orange-400 to-orange-600" />
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 text-orange-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('admin.users.roleChangeConfirmTitle')}
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                {t('admin.users.roleChangeConfirmMessage', {
                  from: roleLabel(roleConfirm.oldRole),
                  to: roleLabel(roleConfirm.newRole),
                  user: roleConfirm.userLabel,
                })}
              </p>
              <div className="bg-orange-50 rounded-lg px-4 py-2 text-sm font-medium text-orange-800 text-center">
                {roleConfirm.oldRole} → {roleConfirm.newRole}
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={async () => {
                    setRoleConfirm(null)
                    await doSave()
                  }}
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-xl font-medium text-sm text-white bg-orange-500 hover:bg-orange-600 transition-colors disabled:opacity-50"
                >
                  {t('common.confirm')}
                </button>
                <button
                  onClick={() => setRoleConfirm(null)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
